import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export default function ScrollDownIndicator({ bottom = 80, right, containerRef, position = 'fixed', center = false, onVisibleChange }) {
    const [visible, setVisible] = useState(false);

    const getScrollEl = useCallback(
        () => containerRef?.current ?? document.scrollingElement ?? document.documentElement,
        [containerRef],
    );

    const checkScroll = useCallback(() => {
        const el = getScrollEl();
        if (!el) return;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        // 스크롤 가능한 영역이 50px 이상 남아있을 때만 표시
        setVisible(prev => {
            const next = remaining > 50;
            if (prev !== next && onVisibleChange) onVisibleChange(next);
            return next;
        });
    }, [getScrollEl, onVisibleChange]);

    useEffect(() => {
        checkScroll();
        const scrollTarget = containerRef?.current ?? window;
        const observeTarget = containerRef?.current ?? document.body;
        scrollTarget.addEventListener('scroll', checkScroll, { passive: true });
        window.addEventListener('resize', checkScroll);
        // 콘텐츠 변경 감지 (데이터 로딩 후 등)
        const observer = new MutationObserver(checkScroll);
        observer.observe(observeTarget, { childList: true, subtree: true });
        return () => {
            scrollTarget.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
            observer.disconnect();
        };
    }, [checkScroll, containerRef]);

    const handleClick = () => {
        const amount = ((containerRef?.current?.clientHeight) ?? window.innerHeight) * 0.6;
        if (containerRef?.current) {
            containerRef.current.scrollBy({ top: amount, behavior: 'smooth' });
        } else {
            window.scrollBy({ top: amount, behavior: 'smooth' });
        }
    };

    if (!visible) return null;

    // 기본 fixed면 화면 가운데. absolute는 호출자가 center를 명시한 경우만 가운데 배치한다.
    const useCenter = right === undefined && (center || position === 'fixed');

    return (
        <Box
            onClick={handleClick}
            role="button"
            tabIndex={0}
            sx={{
                position,
                bottom,
                ...(useCenter
                    ? { left: '50%' }
                    : { right: right || (position === 'fixed' ? 'calc(50% - min(240px, 50vw) + 20px)' : 20) }),
                width: 40,
                height: 40,
                zIndex: 1000,
                cursor: 'pointer',
                // 중앙 배치 시 translateX(-50%)를 keyframes에 포함해 bounce 동작 유지
                animation: useCenter
                    ? 'scrollBounceCentered 1.2s ease-in-out infinite'
                    : 'scrollBounce 1.2s ease-in-out infinite',
                transition: 'opacity 0.3s ease',
                '@keyframes scrollBounce': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%':      { transform: 'translateY(10px)' },
                },
                '@keyframes scrollBounceCentered': {
                    '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
                    '50%':      { transform: 'translateX(-50%) translateY(10px)' },
                },
                '@keyframes scrollPulse': {
                    '0%':   { transform: 'scale(1)',   opacity: 0.55 },
                    '100%': { transform: 'scale(1.7)', opacity: 0 },
                },
                '&:active': { opacity: 0.6 },
            }}
        >
            <Box sx={{
                position: 'absolute', inset: 0,
                borderRadius: '50%',
                border: '2px solid rgba(0,0,0,0.22)',
                pointerEvents: 'none',
                animation: 'scrollPulse 1.5s ease-out infinite',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0,
                borderRadius: '50%',
                border: '2px solid rgba(0,0,0,0.22)',
                pointerEvents: 'none',
                animation: 'scrollPulse 1.5s ease-out 0.75s infinite',
            }} />
            <Box sx={{
                width: 40, height: 40,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.98)',
                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
            }}>
                <KeyboardArrowDownIcon sx={{ color: '#1a1a1a', fontSize: 24 }} />
            </Box>
        </Box>
    );
}
